import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { Modal, Button, Stack, Text } from '@mantine/core';
import { useAtom } from 'jotai';
import { authModalOpenAtom, authUserAtom } from '@/stores/atoms/authAtoms';
import { googleSystemSignIn } from '@/utils/firebase';
import { loginUserWithProvider, setStoredAuthToken, updateMyAccount } from '@/utils/userApi';
const AuthModal = () => {
    const [opened, setOpened] = useAtom(authModalOpenAtom);
    const [, setUser] = useAtom(authUserAtom);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(null);
    const isDev = globalThis?.process?.env?.NODE_ENV === 'development' || (typeof window !== 'undefined' && /localhost|127\.0\.0\.1/.test(window.location?.hostname || ''));
    React.useEffect(() => {
        if (!opened && loading)
            setLoading(false);
        if (!opened && error)
            setError(null);
    }, [opened]);
    const randomDisplayName = () => {
        const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
        let s = '';
        for (let i = 0; i < 6; i++)
            s += chars.charAt(Math.floor(Math.random() * chars.length));
        return `user_${s}`;
    };
    const withTimeout = async (p, ms = 75000) => {
        let timer;
        try {
            return await Promise.race([
                p,
                new Promise((_, reject) => {
                    timer = setTimeout(() => reject(new Error('登录已取消或超时')), ms);
                }),
            ]);
        }
        finally {
            if (timer)
                clearTimeout(timer);
        }
    };
    const doGoogle = async () => {
        setLoading(true);
        setError(null);
        try {
            const u = await withTimeout(googleSystemSignIn());
            // Bridge to backend OAuth-style login (create on first login)
            const gen = randomDisplayName();
            const { user, token } = await loginUserWithProvider({ provider: 'google', provider_id: u.uid, email: u.email, display_name: gen });
            setStoredAuthToken(token);
            // Do NOT adopt provider profile; use server value (random if first-time)
            let displayName = user.display_name || '';
            if (!displayName) {
                try {
                    const updated = await updateMyAccount({ display_name: gen });
                    displayName = updated.display_name || gen;
                }
                catch { }
            }
            setUser({ uid: user.id, displayName: displayName || 'User', email: user.email, photoURL: undefined });
            try {
                localStorage.setItem('firebase_uid', user.id);
            }
            catch { }
            setOpened(false);
        }
        catch (e) {
            const msg = e?.message || 'Google sign-in failed';
            setError(msg);
        }
        finally {
            setLoading(false);
        }
    };
    const doGuest = async () => {
        setLoading(true);
        setError(null);
        try {
            // 检查 localStorage 是否有匿名账号
            let existingAnonymousUid = null;
            try {
                existingAnonymousUid = localStorage.getItem('anonymous_uid');
            }
            catch { }
            const uid = existingAnonymousUid || `guest_${Math.random().toString(36).slice(2, 10)}_${Date.now().toString(36)}`;
            const { user, token } = await loginUserWithProvider({ provider: 'anonymous', provider_id: uid, email: null, display_name: randomDisplayName() });
            setStoredAuthToken(token);
            const gen = randomDisplayName();
            let displayName = user.display_name || '';
            if (!displayName) {
                try {
                    const updated = await updateMyAccount({ display_name: gen });
                    displayName = updated.display_name || gen;
                }
                catch { }
            }
            setUser({ uid: user.id, displayName: displayName || 'User', email: user.email, photoURL: undefined });
            if (!existingAnonymousUid) {
                try {
                    localStorage.setItem('anonymous_uid', uid);
                }
                catch { }
            }
            setOpened(false);
        }
        catch (e) {
            if (isDev) {
                // Dev fallback: create a local guest identity without Firebase
                const uid = `guest_${Math.random().toString(36).slice(2, 10)}_${Date.now().toString(36)}`;
                setUser({ uid, displayName: 'Dev Guest', email: null, photoURL: null });
                try {
                    localStorage.setItem('firebase_uid', uid);
                }
                catch { }
                setOpened(false);
            }
            else {
                setError(e?.message || 'Guest sign-in failed');
            }
        }
        finally {
            setLoading(false);
        }
    };
    const termsUrl = 'https://mallorb.com/terms';
    const privacyUrl = 'https://mallorb.com/privacy';
    return (_jsx(Modal, { opened: opened, onClose: () => setOpened(false), title: "\u767B\u5F55 / \u6CE8\u518C", centered: true, zIndex: 12000, withinPortal: true, overlayProps: { opacity: 0.5, blur: 2 }, children: _jsxs(Stack, { gap: "sm", children: [error && _jsx(Text, { c: "red", size: "sm", children: error }), _jsx(Button, { variant: "default", onClick: doGoogle, loading: loading, children: "\u4F7F\u7528 Google \u767B\u5F55" }), _jsxs(Button, { variant: "light", onClick: doGuest, loading: loading, mt: "xs", children: ["\u533F\u540D\u767B\u5F55", isDev ? '（开发环境可直接使用）' : ''] }), _jsxs(Text, { size: "xs", c: "dimmed", ta: "center", mt: "xs", children: ["\u7EE7\u7EED\u5373\u8868\u793A\u60A8\u540C\u610F\u6211\u4EEC\u7684", ' ', _jsx("a", { href: termsUrl, target: "_blank", rel: "noreferrer", style: { color: 'inherit', textDecoration: 'underline' }, children: "\u6761\u6B3E" }), ' ', "\u5E76\u786E\u8BA4\u6211\u4EEC\u7684", ' ', _jsx("a", { href: privacyUrl, target: "_blank", rel: "noreferrer", style: { color: 'inherit', textDecoration: 'underline' }, children: "\u9690\u79C1\u534F\u8BAE" })] })] }) }));
};
export default AuthModal;
